import Bugsnag from '@bugsnag/js';

function buildRequestMetadata(resource, options = {}, extraMetadata = {}) {
    return {
        url: resource,
        method: options.method || 'GET',
        headers: options.headers,
        payload: options.body,
        ...extraMetadata,
    };
}

export function attachMetadata(error, key, metadata) {
    if (!error || !metadata) {
        return error;
    }

    error[key] = {
        ...(error[key] || {}),
        ...metadata,
    };

    return error;
}

export function isTransientNetworkError(error) {
    if (!(error instanceof Error)) {
        return false;
    }

    if (error.name === 'AbortError') {
        return true;
    }

    return error instanceof TypeError && error.message === 'Failed to fetch';
}

function getBackendErrorMessage(responseBody) {
    if (!responseBody) {
        return null;
    }

    if (typeof responseBody === 'string') {
        return responseBody;
    }

    if (typeof responseBody !== 'object') {
        return null;
    }

    return responseBody.message || responseBody.error || responseBody.detail || null;
}

function getBackendErrorCode(responseMetadata = {}) {
    return responseMetadata.code ?? responseMetadata.status ?? null;
}

function normalizeReportableError(error) {
    const reportableError = error instanceof Error ? error : new Error(String(error));
    const backendMessage = getBackendErrorMessage(reportableError.responseMetadata?.body);
    const backendCode = getBackendErrorCode(reportableError.responseMetadata);

    if (!backendMessage && backendCode === null) {
        return reportableError;
    }

    const normalizedMessage = [backendCode, backendMessage || reportableError.message]
        .filter(Boolean)
        .join(': ');

    reportableError.message = normalizedMessage;
    reportableError.code = backendCode ?? reportableError.code;
    reportableError.status = reportableError.responseMetadata?.status ?? reportableError.status;

    return reportableError;
}

export async function performRequest(resource, options = {}, extraMetadata = {}) {
    const requestMetadata = buildRequestMetadata(resource, options, extraMetadata);

    try {
        const response = await fetch(resource, options);
        response.requestMetadata = requestMetadata;
        return response;
    } catch (error) {
        throw attachMetadata(error, 'requestMetadata', requestMetadata);
    }
}

export function notifyError(error, extraMetadata = {}) {
    const reportableError = normalizeReportableError(error);

    if (error?.requestMetadata) {
        attachMetadata(reportableError, 'requestMetadata', error.requestMetadata);
    }

    if (error?.responseMetadata) {
        attachMetadata(reportableError, 'responseMetadata', error.responseMetadata);
    }

    Bugsnag.notify(reportableError, (event) => {
        if (reportableError.requestMetadata) {
            event.addMetadata('request', reportableError.requestMetadata);
        }

        if (reportableError.responseMetadata) {
            event.addMetadata('response', reportableError.responseMetadata);
        }

        if (reportableError.code !== undefined || reportableError.status !== undefined) {
            event.addMetadata('error', {
                code: reportableError.code,
                status: reportableError.status,
            });
        }

        if (isTransientNetworkError(reportableError)) {
            event.addMetadata('network', {
                transient: true,
                online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
            });
        }

        Object.entries(extraMetadata).forEach(([section, metadata]) => {
            event.addMetadata(section, metadata);
        });
    });
}

export function getListNetworkErrorMessage(type) {
    if (type === 'assessments') {
        return 'assessmentsLoadFailed';
    }

    return 'coursesLoadFailed';
}

export function getDetailNetworkToast(type) {
    if (type === 'assessment') {
        return 'assessmentLoadFailed';
    }

    return 'courseLoadFailed';
}
